import { useState, useRef, useEffect } from 'react';
import { User, Settings, Moon, Sun, Monitor, ChevronDown, BarChart3, Workflow, Database } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { useNavigate } from 'react-router-dom';

export default function UserMenu() {
  const { preferences, setTheme, userProfile } = useAppStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    setTheme(theme);
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      // System preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  const handleNavigateToSettings = () => {
    navigate('/settings');
    setIsOpen(false);
  };

  const handleNavigateToAnalytics = () => {
    navigate('/analytics');
    setIsOpen(false);
  };

  const handleNavigateToAutomations = () => {
    navigate('/automations');
    setIsOpen(false);
  };

  const handleNavigateToAdmin = () => {
    navigate('/admin');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-recipe-card transition-colors"
      >
        {userProfile?.avatar ? (
          <img
            src={userProfile.avatar}
            alt={userProfile.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 bg-recipe-accent rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
        )}
        <ChevronDown className={`w-4 h-4 text-recipe-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-recipe-card border border-recipe-border rounded-xl shadow-2xl overflow-hidden z-50 animate-slide-up">
          {/* User Info */}
          <div className="p-4 border-b border-recipe-border">
            <div className="flex items-center gap-3">
              {userProfile?.avatar ? (
                <img
                  src={userProfile.avatar}
                  alt={userProfile.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-recipe-accent rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-recipe-text truncate">
                  {userProfile?.name || 'Recipe Labs User'}
                </h4>
                <p className="text-xs text-recipe-text-muted truncate">
                  {userProfile?.email || 'user@recipelabs.com'}
                </p>
                {userProfile?.role && (
                  <span className="inline-flex items-center px-2 py-0.5 mt-1 text-xs font-medium rounded-full bg-recipe-accent/10 text-recipe-accent">
                    {userProfile.role}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Theme Selector */}
          <div className="p-4 border-b border-recipe-border">
            <label className="text-xs font-semibold text-recipe-text-muted uppercase tracking-wide mb-2 block">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleThemeChange('light')}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all ${
                  preferences.theme === 'light'
                    ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent'
                    : 'border-recipe-border hover:border-recipe-accent/50 text-recipe-text-muted'
                }`}
              >
                <Sun className="w-4 h-4" />
                <span className="text-xs font-medium">Light</span>
              </button>
              <button
                onClick={() => handleThemeChange('dark')}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all ${
                  preferences.theme === 'dark'
                    ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent'
                    : 'border-recipe-border hover:border-recipe-accent/50 text-recipe-text-muted'
                }`}
              >
                <Moon className="w-4 h-4" />
                <span className="text-xs font-medium">Dark</span>
              </button>
              <button
                onClick={() => handleThemeChange('system')}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all ${
                  preferences.theme === 'system'
                    ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent'
                    : 'border-recipe-border hover:border-recipe-accent/50 text-recipe-text-muted'
                }`}
              >
                <Monitor className="w-4 h-4" />
                <span className="text-xs font-medium">System</span>
              </button>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={handleNavigateToAnalytics}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-recipe-bg transition-colors text-left"
            >
              <BarChart3 className="w-4 h-4 text-recipe-text-muted" />
              <span className="text-sm text-recipe-text">Analytics</span>
            </button>
            <button
              onClick={handleNavigateToAutomations}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-recipe-bg transition-colors text-left"
            >
              <Workflow className="w-4 h-4 text-recipe-text-muted" />
              <span className="text-sm text-recipe-text">Automations</span>
            </button>
            <button
              onClick={handleNavigateToSettings}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-recipe-bg transition-colors text-left"
            >
              <Settings className="w-4 h-4 text-recipe-text-muted" />
              <span className="text-sm text-recipe-text">Settings</span>
            </button>
            <button
              onClick={handleNavigateToAdmin}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-recipe-bg transition-colors text-left"
            >
              <Database className="w-4 h-4 text-recipe-text-muted" />
              <span className="text-sm text-recipe-text">Admin</span>
            </button>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-recipe-border bg-recipe-bg/50">
            <p className="text-xs text-recipe-text-muted text-center">
              Recipe Labs Hub v1.0.0
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
